import React from 'react';
import { IconArrowDownRegular, IconInfoRegular, Text } from '@croquiscom-pvt/zds';
import styled from '@emotion/styled';

import { TouchAccessibilityWrapper } from '@domains/pdp-common';

import { CreditRewards } from '../../types';
import { TextItem } from './TextItem';

interface Props {
  data: CreditRewards[];
}

export const CreditRewardsList = ({ data }: Props) => {
  const [open, setOpen] = React.useState(false);
  return (
    <Container>
      <Text variant='Body_14_Medium' color='textTertiary' style={{ minWidth: 56 }}>
        적립
      </Text>
      <Full>
        <TitleList gap={open ? 16 : 6}>
          {data.map((item, index) => {
            const has_contents = Boolean(item.contents.length);
            const has_badge = Boolean(item.title.badge);
            return (
              <div key={index}>
                <TouchAccessibilityWrapper touchHeight={32} onClick={() => has_contents && setOpen(!open)}>
                  <Title>
                    <TitleInner>
                      {item.title.sections?.map((item, index) => (
                        <TextItem key={index} item={item} variant='Body_14_Regular' color='textStrong' />
                      ))}

                      {has_badge && (
                        <Badge>
                          <Text variant='Caption_10_SemiBold' style={{ color: '#FF950D' }} as='span'>
                            {item.title.badge ?? ''}
                          </Text>
                        </Badge>
                      )}
                    </TitleInner>
                    {has_contents && index === 0 && (
                      <Icon>
                        <IconArrowDownRegular
                          size={20}
                          color='textSecondary'
                          style={{ position: 'relative', top: '-2px', rotate: open ? '180deg' : '', minWidth: 20 }}
                        />
                      </Icon>
                    )}
                  </Title>
                </TouchAccessibilityWrapper>

                {has_contents && open && (
                  <Contents marginTop={open ? 8 : 4}>
                    {item.contents.map((content, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
                        {content.sections?.map((item) => (
                          <TextItem key={item.text} item={item} color='textTertiary' />
                        ))}
                        {content.onClickInfo && (
                          <TouchAccessibilityWrapper
                            touchWidth={30}
                            touchHeight={30}
                            onClick={() => content.onClickInfo?.()}
                          >
                            <div
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', maxHeight: 16 }}
                            >
                              <IconInfoRegular width={20} height={20} color='textTertiary' style={{ marginLeft: 2 }} />
                            </div>
                          </TouchAccessibilityWrapper>
                        )}
                      </div>
                    ))}
                  </Contents>
                )}
              </div>
            );
          })}
        </TitleList>
      </Full>
    </Container>
  );
};

const Container = styled.div`
  margin: 0 12px 0 16px;
  padding: 16px 0;
  display: flex;
  gap: 8px;

  b {
    font-weight: 500;
  }

  .text-container {
    display: flex;
    overflow: hidden;
  }

  .text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: pre;
  }

  .fixed {
    flex-shrink: 0;
  }
`;

const Title = styled.div`
  display: flex;
  justify-content: space-between;
  white-space: pre-wrap;
  gap: 2px;
`;

const Contents = styled.div<{ marginTop: number }>`
  display: flex;
  flex-direction: column;
  row-gap: 6px;
  ${({ marginTop }) => (marginTop ? `margin-top: ${marginTop}px;` : '')};

  .ellipsis {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    word-break: break-all;
    flex: 1;
  }

  .underline {
    text-decoration: underline;
  }
`;

const Full = styled.div`
  width: 100%;
  overflow: hidden;
`;

const TitleInner = styled.div`
  display: flex;
  overflow: hidden;
`;

const Badge = styled.div`
  margin-left: 4px;
  padding: 2px 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 2px;
  background: rgba(255, 149, 13, 0.12);
`;

const Icon = styled.div`
  height: 17px;
`;

const TitleList = styled.div<{ gap: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ gap }) => gap}px;
`;
